import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { SiteConfig } from '../api/types';

interface SessionState {
    config: SiteConfig | null;
    loading: boolean;
    error: string | null;
    retry: () => void;
}

/**
 * Bootstraps the anonymous session and loads the public site configuration
 * (price, currency, PayPal client id, accepted file types).
 *
 * Legacy equivalent: the `main::getPartnerConfigurationData` handshake that ran
 * over the WebSocket before the SPA could render anything.
 */
export function useSession(): SessionState {
    const [config, setConfig] = useState<SiteConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setError(null);

        api.session()
            .then((session) => {
                if (!cancelled) {
                    setConfig(session.config);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'The service is unavailable');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [attempt]);

    const retry = useCallback(() => setAttempt((value) => value + 1), []);

    return { config, loading, error, retry };
}
